import React from 'react';

import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '@/store';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { getBonusCalculator, getEligibleBonuses } from '@/modules/casino/store/actions/application';
import { requestWallet } from '@/modules/casino/store/actions/wallet';
import Bonus from '@/components/classes/Bonus/Bonus';
import { requestAccount, requestLimits } from '@/modules/casino/store/actions';
import Bridger from '@/components/classes/PaymentProviders/Deposit/Bridger/Bridger';
import { AmountButtonInterface } from '@/components/classes/PaymentProviders/Interfaces/AmountButtonInterface';
import { DEFAULT_AMOUNT, PaymentProvider, paymentProviderNameById } from '@/constants/paymentProvider';

import {
  Card,
  PaymentDetailsInterface,
  PaymentProviderInterface,
} from '@/components/classes/PaymentProviders/Deposit/Interfaces/PaymentProviderInterface';
import Viva from '@/components/classes/PaymentProviders/Deposit/Viva/Viva';
import Safecharge from '@/components/classes/PaymentProviders/Deposit/Safecharge/Safecharge';
import Okto from '@/components/classes/PaymentProviders/Deposit/Okto/Okto';
import Abon from '@/components/classes/PaymentProviders/Deposit/Abon/Abon';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { receivedVivaChargeToken } from '@/modules/casino/store/actions/paymentCheckout';
import { useTranslation } from 'react-i18next';
import './index.scss';
import {
  fetchPlayerPaymentMethods,
  getTax,
  oktoGetCustomer,
  resetDeposit,
} from '@/modules/casino/store/actions/deposit';
import Betlion from '@/components/classes/PaymentProviders/Deposit/Betlion/Betlion';
import { getCurrencyNumberInfo, getFormattedCurrencyAmount } from '@/page-components/utils/functions';
import { extractNumberFromFormattedString } from '@/components/modules/deposit/deposit-legacy/functions';
import { fetchRewardBundles } from '@/modules/casino/store/actions/reward_bundle';
import evBusMain from '@/utils/evbus';
import ExpiringLocalStorage from '@/modules/casino/utils/ExpiringLocalStorage';
import { useDepositLegacyEvents } from '@/components/modules/deposit/deposit-legacy/hooks/useDepositLegacyEvents';

type DepositProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    dsType: string;
  };
};

type ModuleStateProps = {
  depositValue: number;
  depositValueWithoutTax: number;
  taxes: number | null;
  selectedBonus: any;
  currentStep: string;
  lastSteps: string[];
  selectedBonusInfo: any;
  selectedCard: any;
  bonuses: any;
  allBonuses: any;
  loading: boolean;
  bonusLoading: boolean;
  cardsLoading: boolean;
  inputsLoading: boolean;
  initialized: boolean;
  paymentProvider: number;
  defaultPaymentProvider: number;
  isOpen: boolean;
  paymentProviderObject: PaymentProviderInterface | null;
  activePaymentProviders: ActivePaymentProvider[];
  tcAccepted: boolean;
  privacyAccepted: boolean;
  maxLimit: number | boolean;
  oldValues: {
    [key: number]: PaymentDetailsInterface;
  };
  bonusCategories?: any;
  oldPaymentProvider?: number;
  cardInfo?: {
    number: string | undefined;
    name: string | undefined;
    expiry: {
      month: string | undefined;
      year: string | undefined;
      formatted: string | undefined;
    };
    cvv: string | undefined;
  };
  allSavedPaymentMethods?: any;
  alternativeInfo: string;
  hasAlternativeInfo: boolean;
  focusedInput?: boolean;
  trigger?: string;
  isUserDefinedAmount: boolean;
  // currencyPosition: string;
  wallet?: any;
};

type ActivePaymentProvider = {
  paymentProviderId: number;
  applePayButton?: {
    hide: boolean;
    onClick: () => void;
  };
  mainButton?: {
    hide: boolean;
  };
  channel?: any;
  status?: string;
};

const defaultProps = {
  className: '',
  styleText: '',
  properties: {
    dsType: '',
  },
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const DEPOSIT_PAYMENT_PROVIDERS_FIRST = '1';
const DEPOSIT_PAYMENT_METHODS_FIRST = '2';
// add more channel details here, if they appear in the future; they will be displayed in the component
const ALLOWED_CHANNEL_DETAILS_KEYS = ['merchant_code', 'payment_reference', 'account_identifier', 'merchant_name'];

const Deposit = (componentProps: DepositProps) => {
  const tmpProps = { ...defaultProps, ...componentProps };
  delete tmpProps.children;
  const props = JSON.parse(JSON.stringify(tmpProps));
  const { children } = componentProps;
  const { t } = useTranslation();

  // params
  const location = useLocation(); // used with new URLSearchParams() (/deposit?param=value)

  const searchParams = new URLSearchParams(location.search);
  const { currentCurrency } = useAppSelector((state) => state.currencies);

  const navigate = useNavigate();

  const forcedStep = searchParams.get('step') ?? null;

  // @ts-ignore
  const forcedAmount = searchParams.get('amount') ? extractNumberFromFormattedString(searchParams.get('amount')) : null;

  const defaultAmount: number = Number(
    forcedAmount ?? window.config?.depositSettings?.defaultAmount?.[currentCurrency] ?? DEFAULT_AMOUNT,
  );

  const dispatch = useAppDispatch();
  const { depositStatus, depositType } = useParams(); // /deposit/:depositStatus? (/deposit/error, /deposit/success, /deposit/pending, /deposit/cancel)
  const profile = useAppSelector((state) => state.profile);
  const { total } = useAppSelector((state) => state.wallet);
  const deposit = useAppSelector((state) => state.deposit);
  const eligibleBonuses = useAppSelector((state) => state.application.eligibleBonuses);
  const rewardBundles = useAppSelector((state) => state.rewardBundle.rewardBundles);

  const bonusAward = useAppSelector((state) => state.application.bonusAward);
  const { taxes } = useAppSelector((state) => state.deposit);

  const initialStep: any = React.useMemo(
    () => ({
      [DEPOSIT_PAYMENT_PROVIDERS_FIRST]: forcedStep ?? 'amount',
      [DEPOSIT_PAYMENT_METHODS_FIRST]: forcedStep ?? 'saved-payment-methods',
    }),
    [forcedStep],
  );

  const statusSteps = ['error', 'success', 'pending'];

  const getPreselectedBonusId = () => {
    if (!state.allBonuses) {
      return false;
    }

    // check if bonusId exists in url as parameter
    const params = new URLSearchParams(location.search);

    let bonusId: any = params.get('bonusId');
    if (bonusId) {
      if (state.allBonuses[bonusId]) {
        return bonusId;
      }
    }

    // check if bonusIds[] exists in url as parameter
    const bonusIds = params.getAll('bonusIds[]');

    bonusId = false;
    for (let i = 0; i < bonusIds.length; i++) {
      // @ts-ignore
      if (state.allBonuses[bonusIds[i]]) {
        // @ts-ignore
        bonusId = bonusIds[i];
        break;
      }
    }

    return bonusId ?? false;
  };

  const initialState = {
    selectedBonus: false,
    // currencyPosition: 'left',
    selectedBonusInfo: {},
    depositValue: defaultAmount,
    depositValueWithoutTax: 0,
    selectedCard: null,
    bonuses: null,
    allBonuses: null,
    taxes: null,
    initialized: false,
    currentStep: initialStep[props.properties.depositType] ?? initialStep[DEPOSIT_PAYMENT_PROVIDERS_FIRST],
    lastSteps: [initialStep[props.properties.depositType] ?? initialStep[DEPOSIT_PAYMENT_PROVIDERS_FIRST]],
    loading: false,
    bonusLoading: true,
    cardsLoading: false,
    inputsLoading: false,
    oldPaymentProvider: 0,
    paymentProvider: 0,
    paymentProviderObject: null,
    defaultPaymentProvider: 0,
    tcAccepted: true,
    privacyAccepted: true,
    oldValues: {},
    bonusCategories: {},
    allSavedPaymentMethods: [],
    activePaymentProviders: [],
    isOpen: false,
    cardInfo: {
      number: '',
      name: '',
      expiry: {
        month: '',
        formatted: '',
        year: '',
      },
      cvv: '',
    },
    maxLimit: false,
    alternativeInfo: '',
    hasAlternativeInfo: false,
    focusedInput: false,
    isUserDefinedAmount: false,
  };

  const [state, setState] = React.useState<ModuleStateProps>(initialState);

  const depositEvents = useDepositLegacyEvents(state);

  const hideLoader = React.useCallback(() => {
    setState((v) => ({
      ...v,
      loading: false,
      trigger: 'hideLoader',
    }));
  }, []);

  const setActivePaymentProviders = () => {
    let selectedProviders = searchParams.getAll('providerId[]');
    if (selectedProviders.length === 0) {
      // if no providerIds[] in url, add all defined in PaymentProvider
      selectedProviders = Object.values(PaymentProvider).map((provider) => provider.toString());
    }
    let loading: boolean | null = null;

    const activePaymentProviders: ActivePaymentProvider[] = [];
    // intersect selectedProviders with active providers
    selectedProviders.forEach((provider: any) => {
      const providerConfig = window.config.depositSettings?.providers?.[parseInt(provider, 10)];
      if (providerConfig?.isActive === '1') {
        const activePaymentProvider: ActivePaymentProvider = {
          paymentProviderId: parseInt(provider, 10),
          applePayButton: {
            hide: true,
            onClick: () => {},
          },
          mainButton: {
            hide: !(providerConfig?.isMainPaymentMethodActive === '1'),
          },
          status: 'default',
        };
        switch (parseInt(provider, 10)) {
          case PaymentProvider.betlion:
            const betlion = new Betlion({
              triggerSuccess: () => 0,
              triggerError: () => 0,
              triggerPending: () => 0,
              dispatch: dispatch,
              hideLoader: () => 0,
              showLoader: showLoader,
            });

            if (deposit.betLion.channels === null) {
              betlion.fetchChannels();
              loading = true;
              activePaymentProviders.push(activePaymentProvider); // this will be replaced when we receive the channels from the server
            } else {
              loading = false;
              deposit.betLion.channels?.map((channel: any) => {
                const newActivePaymentProvider = { ...activePaymentProvider };

                if (channel.channel_id === 'mobile') {
                  newActivePaymentProvider.channel = {
                    ...channel,
                    mno: {
                      player: deposit.betLion.mno.player,
                      // map others with value: value
                      others: deposit.betLion.mno.other,
                    },
                  };
                  activePaymentProviders.push(newActivePaymentProvider);
                } else {
                  if (PaymentProvider[`betlion_${channel.channel_id}`]) {
                    const skipForNow =
                      !!window.config.depositSettings?.providers?.[PaymentProvider[`betlion_${channel.channel_id}`]];

                    if (!skipForNow) {
                      newActivePaymentProvider.channel = channel;
                      newActivePaymentProvider.paymentProviderId = PaymentProvider[`betlion_${channel.channel_id}`];
                      activePaymentProviders.push(newActivePaymentProvider);
                    }
                  }
                }
              });
            }
            break;
          case PaymentProvider.bridger:
            // check localstorage for bridger data
            const bridgerApplePayEnabled = window.localStorage.getItem('bridgerApplePayEnabled');
            if (bridgerApplePayEnabled) {
              providerConfig.isApplePayActive = bridgerApplePayEnabled;
            }
            if (providerConfig.isApplePayActive === '1') {
              const ap = new Bridger({
                triggerSuccess: () => 0,
                triggerError: () => 0,
                triggerPending: () => 0,
                dispatch: () => 0,
                hideLoader: () => 0,
                showLoader: () => 0,
                settings: {
                  amount: state.depositValue,
                  bonusId: state.selectedBonus,
                },
              })?.getApplePay?.();
              if (ap) {
                activePaymentProvider.applePayButton = {
                  hide: !ap.isApplePayAvailable(),
                  onClick: ap.onApplePayButtonClicked,
                };
              }
            }
            if (!(activePaymentProvider.applePayButton?.hide && activePaymentProvider.mainButton?.hide)) {
              activePaymentProviders.push(activePaymentProvider);
            }
            break;
          case PaymentProvider.viva:
            if (providerConfig?.isApplePayActive === '1') {
              const ap = new Viva({
                triggerSuccess: () => 0,
                triggerError: () => 0,
                triggerPending: () => 0,
                dispatch: () => 0,
                hideLoader: () => 0,
                showLoader: () => 0,
              }).initVivaApplePay({
                amount: defaultAmount,
                bonusId: null,
              });

              if (ap) {
                activePaymentProvider.applePayButton = {
                  hide: !ap.isApplePayAvailable(),
                  onClick: ap.onApplePayButtonClicked,
                };
              }
            }
            if (!(activePaymentProvider.applePayButton?.hide && activePaymentProvider.mainButton?.hide)) {
              activePaymentProviders.push(activePaymentProvider);
            }
            break;
          default:
            activePaymentProviders.push(activePaymentProvider);
            break;
        }
      }
    });

    // sort activePaymentProviders by window.config.depositSettings.providers order
    activePaymentProviders.sort((a, b) => {
      const aOrder = window.config.depositSettings.providers[a.paymentProviderId]?.order
        ? parseInt(window.config.depositSettings.providers[a.paymentProviderId].order, 10)
        : 9999;
      const bOrder = window.config.depositSettings.providers[b.paymentProviderId]?.order
        ? parseInt(window.config.depositSettings.providers[b.paymentProviderId].order, 10)
        : 9999;
      return aOrder - bOrder;
    });

    const defaultPaymentProvider = activePaymentProviders?.[0]?.paymentProviderId;

    setState((v) => ({
      ...v,
      paymentProvider: defaultPaymentProvider,
      defaultPaymentProvider: defaultPaymentProvider,
      activePaymentProviders: activePaymentProviders,
      trigger: 'setActivePaymentProviders',
    }));
    if (loading !== null) {
      if (loading) {
        showLoader();
      } else {
        hideLoader();
      }
    }
  };

  const changeActivePaymentProviderStatus = React.useCallback(
    (status: string) => {
      setState((v) => {
        const activePaymentProviders: ActivePaymentProvider[] = [];
        v.activePaymentProviders.forEach((provider: ActivePaymentProvider) => {
          activePaymentProviders.push({
            ...provider,
            status: provider.paymentProviderId === state.paymentProvider ? status : provider.status,
          });
        });

        return {
          ...v,
          activePaymentProviders: activePaymentProviders,
          trigger: 'changeActivePaymentProviderStatus',
        };
      });
    },
    [state],
  );

  React.useEffect(() => {
    let bonuses = eligibleBonuses ?? {};

    if (rewardBundles) {
      // add rewardBundles to bonuses in the beginning of the array
      bonuses = {
        '-1': { ...rewardBundles[0] },
        ...bonuses,
      };
    }

    setState((v) => ({
      ...v,
      allBonuses: bonuses,
      trigger: 'setAllBonuses',
    }));
  }, [rewardBundles, eligibleBonuses]);

  const triggerError = React.useCallback(
    (error: any = null) => {
      window.parent.postMessage({ type: 'deposit-response', status: 'error' }, '*');
      // check if the error is on initial steps; we should deactivate the provider, not trigger the error!
      if (Object.values(initialStep).includes(state.currentStep)) {
        if (error && error.soft) {
          console.warn('Deposit Soft Error received', error);
          changeActivePaymentProviderStatus('error');
        } else {
          setState((v) => ({
            ...v,
            currentStep: 'error',
            errorMessageForEvents: error,
            trigger: 'triggerError',
          }));
        }
      } else {
        setState((v) => ({
          ...v,
          currentStep: 'error',
          errorMessageForEvents: error,
          trigger: 'triggerError',
        }));
      }

      hideLoader();
    },
    [changeActivePaymentProviderStatus, hideLoader, initialStep, state.currentStep],
  );

  const triggerSuccess = React.useCallback(() => {
    const fgm = ExpiringLocalStorage.get('fgm');

    let step = 'success';
    switch (state.paymentProviderObject?.getType()) {
      case PaymentProvider.okto:
        step = 'success-okto';
        break;
      case PaymentProvider.betlion_ekwanza:
        step = 'success-ekwanza';
        break;
      case PaymentProvider.betlion_paypay:
        step = 'success-paypay';
        break;
      case PaymentProvider.betlion:
        step = 'success-betlion-mobile';
        break;
      case PaymentProvider.betlion_paychangu:
        step = 'success-paychangu';
        break;
      default:
        if (fgm) {
          // navigate(fgm);
          // depositEvents.successEvent(); // force event here;
          setState((v) => ({
            ...v,
            currentStep: step,
            trigger: 'triggerSuccess-fgm',
          }));
          setTimeout(() => {
            if (depositStatus) {
              // if depositStatus is set, we should navigate to it using navigate
              navigate(fgm);
            } else {
              // otherwise, we should emit the event, because we have history in the app;
              evBusMain.emit('route-change', { url: fgm });
            }
          }, 100);

          return;
        }
        break;
    }
    ExpiringLocalStorage.remove('fgm');
    setState((v) => ({
      ...v,
      currentStep: step,
      trigger: 'triggerSuccess',
    }));
    hideLoader();
    // call api to get wallet
    setTimeout(() => {
      dispatch(requestWallet());
    }, 500);
  }, [state.paymentProviderObject, hideLoader, depositStatus, navigate, dispatch]);

  const triggerPending = React.useCallback(() => {
    setState((v) => ({
      ...v,
      currentStep: 'pending',
      trigger: 'triggerPending',
    }));
    hideLoader();
  }, [hideLoader]);

  const showLoader = React.useCallback(() => {
    setState((v) => ({
      ...v,
      loading: true,
      trigger: 'showLoader',
    }));
  }, []);

  const showInputsLoader = React.useCallback(() => {
    if (!state.inputsLoading) {
      setState((v) => ({
        ...v,
        inputsLoading: true,
        trigger: 'showInputsLoader',
      }));
    }
  }, [state]);

  const hideInputsLoader = React.useCallback(() => {
    setState((v) => ({
      ...v,
      inputsLoading: false,
      trigger: 'hideInputsLoader',
    }));
  }, []);

  React.useEffect(() => {
    setActivePaymentProviders();

    if (props.properties.depositType === DEPOSIT_PAYMENT_METHODS_FIRST) {
      dispatch(fetchPlayerPaymentMethods());
    }
    if (profile?.client_player_id) {
      initComponent();
    }
    if (state.currentStep === 'deposit-bridger') {
      state.paymentProviderObject?.fetchCards?.();
    }

    if (
      ['deposit-bridger', 'deposit-bridger-card', 'amount'].includes(state.currentStep) &&
      state.paymentProviderObject?.getType?.() === PaymentProvider.bridger
    ) {
      // after setting payment details, Apple Pay session is initialized automatically
      state.paymentProviderObject?.setPaymentDetails({
        amount: state.depositValue,
        bonusId: getPreselectedBonusId(),
      });
      // state.paymentProviderObject?.getApplePay?.()?.startSession?.();
    }

    dispatch(requestLimits());

    // used to refresh profile ftd
    dispatch(requestAccount());

    if (location.search) {
      // store the path to the game we're coming from so we can return to it after deposit
      const params = new URLSearchParams(location.search);
      const fromGame = params.get('from_game');
      if (fromGame != null) {
        ExpiringLocalStorage.set('fgm', fromGame, 600);
      } else {
        if (depositStatus == undefined) {
          // if we don't have a from_game param, we should remove it from localStorage
          // localStorage.removeItem('fgm');
        }
      }
    }
    // setCurrencyPosition();
    return () => {
      dispatch(resetDeposit());
      state.paymentProviderObject?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React.useEffect(() => {
  //   setCurrencyPosition();
  // },[currentCurrency]);

  React.useEffect(() => {
    if (state.loading) {
      // scroll to top, smooth
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [state.loading]);

  React.useEffect(() => {
    if (props.properties.depositType === DEPOSIT_PAYMENT_METHODS_FIRST) {
      if (!deposit.paymentMethods.loading) {
        const activePaymentProviderIds = state.activePaymentProviders.map((provider) => provider.paymentProviderId);
        let paymentMethods: ActivePaymentProvider[] = [];
        if (typeof deposit.paymentMethods.all.error === 'undefined') {
          paymentMethods = deposit.paymentMethods.all.filter((method: any) =>
            activePaymentProviderIds.includes(parseInt(method.paymentMethodType)),
          );
        }
        // loop through payment methods and create card objects
        const cards = paymentMethods?.map((card: any) => {
          const tempPaymentProviderObject = getPaymentProviderByMethodType(parseInt(card.paymentMethodType), null);
          tempPaymentProviderObject?.setCards([card.paymentMethodDetails]);
          const all = tempPaymentProviderObject?.getCards();
          return all && all.length ? all[0] : null;
        });
        if (cards && cards.length > 0) {
          cards[0]?.selectCard?.();

          setState((v) => ({
            ...v,
            allSavedPaymentMethods: cards ?? [],
            currentStep: statusSteps.includes(v.currentStep)
              ? v.currentStep
              : (initialStep[DEPOSIT_PAYMENT_METHODS_FIRST] ?? 'amount'),
            trigger: 'paymentReceived_cards',
          }));
        } else {
          setState((v) => ({
            ...v,
            allSavedPaymentMethods: [],
            currentStep: statusSteps.includes(v.currentStep)
              ? v.currentStep
              : (initialStep[DEPOSIT_PAYMENT_PROVIDERS_FIRST] ?? 'amount'),
            trigger: 'paymentReceived_no_cards',
          }));
          checkDepositStatus();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.paymentMethods.all, state.activePaymentProviders]); // DEPOSIT_PAYMENT_METHODS_FIRST

  React.useEffect(() => {
    if (state.selectedBonus && bonusAward?.bonusId == state.selectedBonus && state.allBonuses?.[state.selectedBonus]) {
      const bonus = new Bonus(state.allBonuses[state.selectedBonus], bonusAward.awardAmount ? bonusAward : false);
      const selectedBonusInfo = getBonusInfo(bonus, true, state.paymentProvider, bonusAward.awardAmount ?? 0);

      setState((v) => ({
        ...v,
        selectedBonusInfo: selectedBonusInfo,
        trigger: 'bonusAwardReceived',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bonusAward, state.allBonuses]);

  React.useEffect(() => {
    updateTax(taxes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxes]);

  // const setCurrencyPosition = ()=> {
  //   const currencyPosition = getCurrencyNumberInfo({locale:'',amount:'1',currency: currentCurrency,digits: ''}).types?.[0] === 'a' ? 'right' : 'left';
  //   setState((v) => ({
  //     ...v,
  //     currencyPosition: currencyPosition,
  //     trigger: 'set-currencyPosition',
  //   }));
  // };

  const updateTax = React.useCallback(
    (taxes: any) => {
      const currentTax = taxes[state.depositValue] ?? 0;
      setState((v) => {
        let depositWithoutTax = v.depositValue - currentTax;
        if (depositWithoutTax < 0) {
          depositWithoutTax = 0;
        }

        return {
          ...v,
          depositValueWithoutTax: depositWithoutTax,
          taxes: currentTax,
          trigger: 'updateTax',
        };
      });
    },
    [state],
  );

  React.useEffect(() => {
    // loop through limits and get max limit
    const MAX_LIMIT = 999999;
    let maxLimit = MAX_LIMIT;
    if (profile.limits) {
      Object.keys(profile.limits).map((key) => {
        if (profile.limits[key].limit_amount) {
          const diff = profile.limits[key].limit_amount - profile.limits[key].accumulated_amount;
          if (diff < maxLimit) maxLimit = diff;
        }
      });
    }
    setState((v) => ({
      ...v,
      maxLimit: maxLimit === MAX_LIMIT ? false : maxLimit,
      trigger: 'updateMaxLimit',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.limits]);

  React.useEffect(() => {
    initComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.client_player_id]);

  React.useEffect(() => {
    initPaymentProvider();
    resetBonus();
    if (state.paymentProviderObject?.getType() === PaymentProvider.bridger) {
      state.paymentProviderObject?.setAmount(state.depositValue);
    }
    if (props.properties.depositType === DEPOSIT_PAYMENT_METHODS_FIRST && state.currentStep === 'amount') {
      state.paymentProviderObject?.initInputsForNewCard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.paymentProviderObject]);

  React.useEffect(() => {
    if (state.selectedBonus) {
      const bonus = new Bonus(state.allBonuses[state.selectedBonus]);
      const bonusId = bonus.getBonusId();
      const rewardId = bonus.getRewardId();
      state.paymentProviderObject?.setBonusId(bonusId);
      state.paymentProviderObject?.setRewardId(rewardId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedBonus, state.selectedBonusInfo]);

  // const refreshBonusInfo = React.useCallback(() => {
  //   setState((v) => ({
  //     ...v,
  //     selectedBonusInfo: getBonusInfo(new Bonus(v.selectedBonus), true, v.paymentProvider),
  //     trigger: 'refreshBonusInfo',
  //   }));
  // }, [state]);

  React.useEffect(() => {
    const isAnyInitialStep = Object.values(initialStep).includes(state.currentStep);
    if (window.config.depositLogs === '1') {
      console.log(
        '[Deposit Logs] useEffect state.depositValue',
        state.currentStep,
        isAnyInitialStep,
        state.paymentProviderObject,
      );
    }
    if ((isAnyInitialStep || state.currentStep === 'final-step') && state.depositValue > 0) {
      state.paymentProviderObject?.setAmount(state.depositValue);
      if (state.selectedBonus) {
        selectBonus(state.selectedBonus, state.paymentProvider);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.depositValue]);

  // this is just for betlion
  React.useEffect(() => {
    if (deposit.betLion.mobile.status !== null) {
      switch (deposit.betLion.mobile.status) {
        case 2:
          triggerSuccess();
          // triggerPending();
          break;
        case 1:
          triggerSuccess();
          break;
        default:
          triggerError();
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.betLion.mobile]);

  React.useEffect(() => {
    if (deposit.betLion.ekwanza.paymentCode !== null) {
      triggerSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.betLion.ekwanza]);

  React.useEffect(() => {
    if (deposit.betLion.paypay.url !== null) {
      triggerSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.betLion.paypay]);

  React.useEffect(() => {
    if (deposit.betLion.paychangu.account_number !== null) {
      triggerSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.betLion.paychangu]);

  React.useEffect(() => {
    if (deposit.betLion.channels) {
      setActivePaymentProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.betLion.channels]);

  React.useEffect(() => {
    if (state.paymentProviderObject?.getType() === PaymentProvider.betlion_express) {
      // @ts-ignore
      state.paymentProviderObject.fetchAlternativePhoneNumber();
      showInputsLoader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.hasAlternativeInfo]);

  React.useEffect(() => {
    if (deposit.betLion.express.alternativePhoneNumber) {
      hideInputsLoader();
      setState((v) => ({
        ...v,
        alternativeInfo: deposit.betLion.express.alternativePhoneNumber,
        trigger: 'alternativeInfoReceived',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.betLion.express.alternativePhoneNumber]);

  // this is just for bridger
  React.useEffect(() => {
    if (deposit.bridger.cashierToken) {
      initPaymentProvider();
      if (
        state.currentStep === 'deposit-bridger-card' ||
        state.currentStep === initialStep[DEPOSIT_PAYMENT_PROVIDERS_FIRST]
      ) {
        state.paymentProviderObject?.initInputsForNewCard();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.bridger.cashierToken]);

  React.useEffect(() => {
    if (window.config.depositLogs === '1') {
      console.log('[Deposit Logs] useEffect received bridgerApplePayInfo', deposit.bridgerApplePay);
    }
    if (deposit.bridgerApplePay.cashierToken && deposit.bridgerApplePay.cashierKey) {
      const bonus = new Bonus(state.allBonuses[state.selectedBonus]);
      state.paymentProviderObject?.initApplePay?.({
        ...deposit.bridgerApplePay,
        amount: state.depositValue,
        bonusId: bonus.getBonusId(),
        rewardId: bonus.getRewardId(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.bridgerApplePay.cashierToken, deposit.bridgerApplePay.cashierKey]);

  React.useEffect(() => {
    if (deposit.bridger.status) {
      if (deposit.bridger.has_3ds_challenge) {
        // redirect to 3ds
        state.paymentProviderObject?.redirect(deposit.bridger.url_3ds);
      } else {
        switch (deposit.bridger.status) {
          case 'approved':
            triggerSuccess();
            break;
          case 'pending':
            triggerPending();
            break;
          default:
            triggerError();
            break;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.bridger.status]);

  React.useEffect(() => {
    if (deposit.bridger.error || deposit.bridger.errorCode) {
      triggerError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.bridger.error, deposit.bridger.errorCode]);

  // this is just for paysafe/skrill
  React.useEffect(() => {
    if (deposit.paysafe.iframeUrl) {
      state.paymentProviderObject?.redirect(deposit.paysafe.iframeUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.paysafe.iframeUrl]);

  React.useEffect(() => {
    if (deposit.paysafe.error) {
      triggerError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.paysafe.error]);

  // this is just for aircash
  React.useEffect(() => {
    if (deposit.aircash.iframeUrl) {
      state.paymentProviderObject?.redirect(deposit.aircash.iframeUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.aircash.iframeUrl]);

  React.useEffect(() => {
    if (deposit.aircash.error) {
      triggerError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.aircash.error]);

  // this is just for viva
  React.useEffect(() => {
    if (state.paymentProvider === PaymentProvider.viva) {
      if (deposit.viva.tokenList && state.currentStep === 'deposit-viva') {
        state.paymentProviderObject?.setCards(deposit.viva.tokenList);

        setState((v) => ({
          ...v,
          currentStep: deposit.viva.tokenList?.length ? 'deposit-viva' : 'deposit-viva-card',
          cardsLoading: deposit.viva.loading ?? false,
          trigger: 'vivaTokenListReceived',
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.viva]); // this is just for viva

  React.useEffect(() => {
    if (props.properties.depositType === DEPOSIT_PAYMENT_PROVIDERS_FIRST) {
      if (!deposit.paymentMethods.loading && deposit.paymentMethods.initialized) {
        if (state.paymentProvider === PaymentProvider.viva) {
          if (deposit.viva.tokenList && state.currentStep === 'deposit-viva') {
            //  get cards from paymentProviderObject
            const cards = state.paymentProviderObject?.getCards();
            if (cards?.length !== deposit.viva.tokenList.length) {
              state.paymentProviderObject?.setCards(deposit.viva.tokenList);
              if (deposit.viva?.tokenList?.length) {
                state.paymentProviderObject?.initInputsForSavedCards();
              }
            }

            setState((v) => ({
              ...v,
              currentStep: deposit.viva.tokenList?.length ? 'deposit-viva' : 'deposit-viva-card',
              cardsLoading: deposit.viva.loading ?? false,
              trigger: 'vivaTokenListReceived2',
            }));
          }
        } else if (state.paymentProvider === PaymentProvider.bridger) {
          if (state.currentStep === 'deposit-bridger') {
            //  get cards from paymentProviderObject
            const cards = state.paymentProviderObject?.getCards();
            if (cards?.length !== deposit.bridger?.tokenList?.length) {
              state.paymentProviderObject?.setCards(deposit.bridger?.tokenList ?? []);
              if (deposit.bridger?.tokenList?.length) {
                state.paymentProviderObject?.initInputsForSavedCards();
              }
            }
            setState((v) => ({
              ...v,
              currentStep: deposit.bridger?.tokenList?.length ? 'deposit-bridger' : 'deposit-bridger-card',
              cardsLoading: deposit.paymentMethods.loading ?? false,
              trigger: 'bridgerTokenListReceived',
            }));
          }
        }
      } else {
        setState((v) => ({
          ...v,
          cardsLoading: deposit.paymentMethods.loading ?? false,
          trigger: 'paymentMethodsLoading',
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.paymentMethods.loading, deposit.paymentMethods.all]); // DEPOSIT_PAYMENT_PROVIDERS_FIRST

  // this is just for viva
  React.useEffect(() => {
    if (deposit.viva.serverResponse) {
      if (state.currentStep === 'deposit-viva' || state.currentStep === 'deposit-viva-card') {
        if (deposit.viva.serverResponse === 'success') {
          triggerSuccess();
        } else {
          triggerError({
            message: 'Viva server response was not successful',
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.viva.serverResponse]);

  // this is just for okto
  React.useEffect(() => {
    if (state.paymentProvider === PaymentProvider.okto) {
      setState((v) => ({
        ...v,
        tcAccepted: deposit.okto.customer.okto_terms_accepted,
        privacyAccepted: deposit.okto.customer.okto_terms_accepted, // privacy is terms
        trigger: 'oktoTermsReceived',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.okto.customer]);

  React.useEffect(() => {
    if (state.currentStep === 'deposit-okto') {
      if (deposit.okto.paymentCode) {
        triggerSuccess();
      } else {
        if (deposit.okto.error) {
          triggerError();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.okto]);

  React.useEffect(() => {
    if (
      state.selectedBonus &&
      (!state.selectedBonusInfo.deposit || state.depositValue >= state.selectedBonusInfo.deposit?.min)
    ) {
      const bonus = new Bonus(state.allBonuses[state.selectedBonus]);
      const id = bonus.getBonusId();
      // @ts-ignore
      if (id > 0) {
        dispatch(getBonusCalculator(id, state.depositValue, currentCurrency));
      }
    } else {
      if (state.selectedBonus && bonusAward?.bonusId == state.selectedBonus) {
        const bonus = new Bonus(state.allBonuses[state.selectedBonus]);
        const selectedBonusInfo = getBonusInfo(bonus, true, state.paymentProvider);

        setState((v) => ({
          ...v,
          selectedBonusInfo: selectedBonusInfo,
          trigger: 'useEffect[state.depositValue,state.selectedBonus]',
        }));
      }
    }
    if (state.depositValue > 0) {
      // @ts-ignore
      if (typeof taxes[state.depositValue] === 'undefined') {
        dispatch(getTax(state.depositValue));
      } else {
        updateTax(taxes);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.depositValue, state.selectedBonus]);

  React.useEffect(() => {
    createBonusCategories();
    preselectBonus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.allBonuses, state.activePaymentProviders]);

  React.useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.activePaymentProviders]);

  React.useEffect(() => {
    // check if bonus categories is empty
    if (state.bonusCategories && Object.keys(state.bonusCategories).length) {
      selectBonus(state.selectedBonus, state.paymentProvider);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.bonusCategories]);

  React.useEffect(() => {
    // postMessage
    window.parent.postMessage({ type: 'deposit-step-changed', currentStep: state.currentStep }, '*');

    setState((v: any) => {
      if (v.lastSteps.length < 1) {
        return v;
      }

      let lastSteps = [...v.lastSteps];

      if (v.currentStep === v.lastSteps?.[v.lastSteps.length - 2]) {
        // remove last step
        lastSteps = lastSteps.slice(0, -1);
      }

      const lastStep = lastSteps[lastSteps.length - 1];
      if (lastStep === v.currentStep) {
        if (lastSteps === v.lastSteps) {
          return v;
        } else {
          return {
            ...v,
            lastSteps: [...lastSteps],
          };
        }
      }

      return {
        ...v,
        lastSteps: [...lastSteps, v.currentStep],
        trigger: 'lastSteps',
      };
    });

    switch (state.currentStep) {
      case 'amount':
        if (props.properties.depositType === DEPOSIT_PAYMENT_METHODS_FIRST) {
          state.paymentProviderObject?.initInputsForNewCard();
        }
        if (window.config.depositLogs === '1') {
          console.log(
            '[DEPOSIT LOGS] Apple Pay step amount',
            state.paymentProviderObject,
            state.paymentProviderObject?.getType(),
            state.paymentProvider,
            deposit.bridgerApplePay,
          );
        }
        if (state.paymentProviderObject?.getType() === PaymentProvider.bridger) {
          if (deposit.bridgerApplePay.cashierKey && deposit.bridgerApplePay.cashierToken) {
            const bonus = new Bonus(state.allBonuses[state.selectedBonus]);
            state.paymentProviderObject?.initApplePay?.({
              ...deposit.bridgerApplePay,
              amount: state.depositValue,
              bonusId: bonus.getBonusId(),
              rewardId: bonus.getRewardId(),
            });
          } else {
            state.paymentProviderObject.getApplePay?.()?.startSession?.();
          }
        }
        break;
      case initialStep[DEPOSIT_PAYMENT_METHODS_FIRST]:
        state.paymentProviderObject?.initInputsForSavedCards();
        break;
      case 'final-step':
        showLoader();

        // init payment provider based on url
        const tempString = window.localStorage.getItem('deposit');
        window.localStorage.removeItem('deposit');

        const tempObject = tempString ? JSON.parse(tempString) : [];
        const paymentProvider = tempObject?.paymentProvider ?? false;
        if (paymentProvider) {
          changePaymentProvider(parseInt(paymentProvider));
          setState((v) => ({
            ...v,
            depositValue: tempObject?.amount ? tempObject.amount / 100 : v.depositValue,
            trigger: 'final-step',
          }));
          hideLoader();
          // init send confirmation for viva if exists maybe this needs to be called in the same useEffect as all the above
          if (parseInt(paymentProvider) === PaymentProvider.viva) {
            if (tempObject?.data?.chargeToken) {
              state.paymentProviderObject?.sendConfirmation(tempObject);
            }
          }
        }
        break;
      case 'deposit-bridger':
        state.paymentProviderObject?.initInputsForSavedCards();
        break;
      case 'deposit-viva':
        state.paymentProviderObject?.initInputsForSavedCards();
        break;
      case 'deposit-bridger-card':
        state.paymentProviderObject?.initInputsForNewCard();
        break;
      case 'deposit-viva-card':
        state.paymentProviderObject?.initInputsForNewCard();
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentStep]);

  const init = () => {
    if (state.initialized) {
      return;
    }

    setState((v) => ({
      ...v,
      initialized: true,
      trigger: 'init',
    }));

    if (getPreselectedBonusId()) {
      showLoader();
    } else {
      if (depositType) {
        selectDepositType();
      } else {
        checkDepositStatus();
      }
    }
  };

  React.useEffect(() => {
    if (state.initialized) {
      getBonuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.initialized]);

  const loadEventListeners = () => {
    window.parent.postMessage({ type: 'event-listener', status: 'loaded' }, '*');
    // remove event listener to prevent double loading
    window.removeEventListener('message', processEvents);
    // prevent double loading
    window.addEventListener('message', processEvents, false);
  };

  const processEvents = (e: any) => {
    const data = e.data;
    // window.parent.postMessage(
    //   {
    //     type: 'deposit-message-event-log',
    //     data: data,
    //   },
    //   '*',
    // );
    switch (data.type) {
      case 'deposit-set-payment-details':
        // const { currency } = data.paymentDetails;
        if (data.paymentDetails?.rewardId) {
          data.paymentDetails.bonusId = -1;
        }
        setState((v) => ({
          ...v,
          depositValue: data.paymentDetails.amount,
          selectedBonus: data.paymentDetails?.bonusId ?? null,
          selectedCard: data.paymentDetails?.card ?? null,
          cardInfo: data.paymentDetails?.cardInfo ?? null,
          trigger: 'deposit-set-payment-details',
        }));
        state.paymentProviderObject?.setPaymentDetails(data.paymentDetails);
        break;
      case 'deposit':
        {
          // @ts-ignore
          const details = state.paymentProviderObject.getPaymentDetails();

          if (
            (details.amount =
              data.paymentDetails.amount &&
              // details.currency === data.paymentDetails.currency &&
              details.bonusId === data.paymentDetails.bonusId)
          ) {
            state.paymentProviderObject?.quickDeposit(data.paymentDetails);
          }
        }
        break;
      case 'quick-deposit': {
        // create a semaphore using localstorage to prevent double quick deposit
        if (window.localStorage.getItem('quick-deposit')) {
          return;
        }

        window.localStorage.setItem('quick-deposit', 'true');

        window.parent.postMessage({ type: 'quick-deposit', status: 'hand-shake' }, '*');
        if (data.paymentDetails.amount) {
          state.paymentProviderObject?.quickDeposit(data.paymentDetails);
        }
        break;
      }
      case 'render-apple-button': {
        if (data.paymentDetails.amount) {
          state.paymentProviderObject?.setPaymentDetails(data.paymentDetails);
          const ap = state.paymentProviderObject?.getApplePay?.();
          ap?.startSession?.();
        }
        break;
      }
      default:
        break;
    }
  };

  const selectDepositType = () => {
    if (depositType) {
      setState((v) => ({
        ...v,
        currentStep: depositType,
        trigger: 'selectDepositType',
      }));
      const depositTypeArray = depositType.split('-');
      switch (depositTypeArray[0]) {
        case 'bridger':
          loadEventListeners();
          changePaymentProvider(PaymentProvider.bridger);
          state.paymentProviderObject?.auth?.();
          if (depositTypeArray[1] === 'new') {
            state.paymentProviderObject?.initInputsForNewCard();
          } else if (depositTypeArray[1] === 'saved') {
            state.paymentProviderObject?.fetchCards?.();
            state.paymentProviderObject?.initInputsForSavedCards();
          }
          break;
        default:
          console.log('Unknown deposit type');
          break;
      }
    }
  };

  React.useEffect(() => {
    // in cordova app the url is dynamically changed so
    // we need to check if depositStatus has changed
    if (window.config.cordova && depositStatus) {
      checkDepositStatus();
    }
  }, [depositStatus]);

  React.useEffect(() => {
    if (state?.selectedCard?.providerId) {
      changePaymentProvider(state.selectedCard.providerId);
    }
  }, [state.selectedCard]);

  const checkDepositStatus = () => {
    // get info from url, localstorage, and default values
    const tempString = window.localStorage.getItem('deposit');

    const tempObject = tempString ? JSON.parse(tempString) : [];
    let step = depositStatus ?? tempObject?.step ?? state.currentStep;
    let amount = tempObject?.amount ? tempObject.amount / 100 : defaultAmount;

    if (step === 'cancel') {
      step = initialStep[props.properties.depositType];
    }
    // replace amount from url
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('totalAmount')) {
      amount = extractNumberFromFormattedString(urlParams.get('totalAmount') ?? '0');
    }
    if (urlParams.get('amount')) {
      amount = extractNumberFromFormattedString(urlParams.get('amount') ?? '0');
    }
    // depositStatus is the most important;
    // fallback: localstorage > state > default
    switch (step) {
      case 'success':
        triggerSuccess();
        break;
      case 'pending':
        triggerPending();
        break;
      case 'error':
        triggerError({
          message: 'Deposit failed (from redirect)',
        });
        break;
      case 'final-step':
        // setTimeout(() => {
        setState((v) => ({
          ...v,
          currentStep: step,
          trigger: 'checkDepositStatus',
          // taxes: null,
        }));
        // }, 1000);
        break;

      default:
        break;
    }

    setState((v) => ({
      ...v,
      // currentStep: initialStep[props.properties.depositType],
      depositValue: amount,
      trigger: 'checkDepositStatusGlobal',
      // taxes: null,
    }));

    setTimeout(() => {
      // remove deposit from localstorage
      window.localStorage.removeItem('deposit');
    }, 3000);
  };

  const initComponent = () => {
    dispatch(receivedVivaChargeToken({ serverResponse: false })); // reset vivacharge token
    // init payment provider based on data from url/localstorage/state/defaultValues
    init();
  };

  const getBonuses = () => {
    resetBonus();
    dispatch(getEligibleBonuses()); // get bonuses
    dispatch(fetchRewardBundles()); // get reward bundles
  };

  const toBonuses = React.useCallback(() => {
    setState((v: any) => ({
      ...v,
      currentStep: 'bonuses',
      trigger: 'toBonuses',
    }));
    hideLoader();
  }, []);

  const toAmount = React.useCallback(() => {
    // this function actualy redirects to the last step
    setState((v) => ({
      ...v,
      currentStep: 'amount',
      trigger: 'toAmount',
      // depositValue: v.depositValue ?? defaultAmount, this needs to be removed??
    }));
    hideLoader();
  }, [state]);

  const goBack = React.useCallback(() => {
    if (['success', 'pending', 'error'].includes(state.currentStep)) {
      return tryAgain();
    }
    let backStep = state.lastSteps[state.lastSteps.length - 2];

    if (!['deposit-viva-card', 'deposit-bridger-card'].includes(backStep)) {
      if (!state.paymentProviderObject?.getCards?.()?.length) {
        backStep = initialStep[props.properties.depositType];
      }
    } else if (!['bonuses', 'amount', 'deposit-viva', 'deposit-bridger'].includes(backStep)) {
      backStep = initialStep[props.properties.depositType];
    }

    setState((v) => ({
      ...v,
      currentStep: backStep,
      trigger: 'goBack',
    }));
    hideLoader();
  }, [state]);

  const goToAllSavedMethods = React.useCallback(() => {
    setState((v) => ({
      ...v,
      currentStep: initialStep[DEPOSIT_PAYMENT_METHODS_FIRST],
      trigger: 'goToAllSavedMethods',
    }));
    hideLoader();
  }, [state]);

  const getBonusInfo = React.useCallback(
    (bonusObj: Bonus, selected: boolean, paymentProvider: any, st?: any) => {
      if (!st) {
        st = state;
      }
      let bonus = bonusObj;
      if (!paymentProvider) {
        paymentProvider = st.paymentProvider;
      }
      const paymentProviders = bonus.getBonusPaymentMethodsRaw();
      // check if paymentProviders have payment provider
      if (paymentProviders.length && !paymentProviders.includes(paymentProvider.toString())) {
        bonus = new Bonus(0);
      }

      const bonusId = bonus.getId(); // do not use bonusObj.getBonusId() here
      const selectedBonusInfo = {
        name: bonus.getBonusName(),
        products: bonus.getBonusProducts(),
        primary_message: bonus.getBonusAmountDisplay(),
        secondary_message: bonus.getBonusDescription(),
        end_date: bonus.getBonusEndDate(),
        show_date: bonus.getBonusShowDate(),
        terms_url: bonus.getBonusTermsUrl(),
        cta_text: bonus.getBonusCtaText(),
        onClickCTA: toBonuses,
        image: bonus.getBonusImage(),
        icon: bonus.getBonusIcon(),
        bonus_conflicts: bonus.getBonusConflicts(),
        canBeSelected: !bonus.haveConflicts(),
        buttonInfo: !bonus.haveConflicts()
          ? {
              state: 'default',
              colorMapping: 'accent',
              buttonType: 'filled',
              size: 'large',
              style: 'text',
              shadow: 'depth_3',
              iconLeft: false,
              iconRight: false,
              ctaText: 'Claim Bonus',
              loading: false,
              icon: '',
              onClick: () => {
                if (bonusId === 0) {
                } else {
                  // @ts-ignore
                  selectBonus(bonusId, paymentProvider);
                }
              },
            }
          : {
              state: 'disabled',
              colorMapping: 'inverted',
              buttonType: 'filled',
              size: 'large',
              style: 'text',
              shadow: 'none',
              iconLeft: false,
              iconRight: false,
              ctaText: 'Claim Bonus',
              loading: false,
              icon: '',
            },
        id: bonusId,
        bonusId: bonusId,
        deposit: bonus.getBonusDeposit(),
        bonusAward: bonus.getBonusAmountDisplay(
          bonusAward?.bonusId == bonusId ? (bonusAward ?? 0) : undefined,
          st.depositValue,
        ),
        className: '',
      };

      if (bonusId === 0) {
        if (st.bonusCategories?.[paymentProvider]?.length) {
          selectedBonusInfo.name = `<span style="color:#47B96A">${
            st.bonusCategories[paymentProvider].length
          } ${t('bonuses available')}</span>`;
        } else {
          selectedBonusInfo.name = state.bonusLoading ? 'skeleton' : t('No bonuses available');
          selectedBonusInfo.className = 'no-bonuses-available';
        }
      }

      if (selected) {
        const depositMin = selectedBonusInfo?.deposit?.min ?? 0;

        if (depositMin > st.depositValue) {
          selectedBonusInfo.bonus_conflicts = [
            {
              // message: `${t('Minimum deposit value is')} ${getFormattedCurrencyAmount({amount:selectedBonusInfo.deposit.min, currency:currentCurrency, locale: '', digits: ''})}`,
              message: 'WARNING_BONUS_MINIMUM_DEPOSIT_{{MIN}}',
              translatePlaceholders: {
                MIN: getFormattedCurrencyAmount({
                  amount: depositMin,
                  currency: currentCurrency,
                  locale: '',
                  digits: '',
                }),
              },
            },
          ];
          selectedBonusInfo.canBeSelected = false;
        } else {
          const award = 0;

          selectedBonusInfo.secondary_message = getFormattedCurrencyAmount({
            amount: award,
            currency: currentCurrency,
            locale: '',
            digits: '',
          });
        }
      }

      return selectedBonusInfo;
    },
    [state, bonusAward],
  );

  const resetBonus = () => {
    if (!state.selectedBonus && !state.selectedBonusInfo) {
      // this is an init call
      const bonus = new Bonus(false);
      const bonusInfo = getBonusInfo(bonus, false, state.paymentProvider);

      setState((v) => ({
        ...v,
        selectedBonus: false,
        selectedBonusInfo: bonusInfo,
        trigger: 'resetBonus',
      }));
    }
  };

  const preselectBonus = React.useCallback(() => {
    // based on bonusId parameter from url
    const bonusId = getPreselectedBonusId();

    if (bonusId) {
      // select bonus if exists
      const bonus = new Bonus(state.allBonuses[bonusId]);
      let pm: number[] = bonus.getBonusPaymentMethodsRaw();
      const ids: number[] = state.activePaymentProviders.map((provider) => provider.paymentProviderId);
      if (!pm.length) {
        pm = ids;
      } else {
        pm = pm.filter((id) => ids.includes(id));
      }
      const oldValues = {};
      const bonusInfo = getBonusInfo(bonus, true, state.paymentProvider);
      const depositValue =
        forcedAmount ??
        (bonusInfo.deposit.min && bonusInfo.deposit.min > defaultAmount ? bonusInfo.deposit.min : defaultAmount);
      pm.forEach((paymentMethod: any) => {
        // @ts-ignore
        oldValues[paymentMethod] = {
          selectedBonus: bonusId,
          selectedBonusInfo: bonusInfo,
          depositValue: depositValue,
        };
      });

      // @ts-ignore
      setState((v) => ({
        ...v,
        selectedBonus: bonusId,
        // currentStep: 'amount',
        depositValue: depositValue,
        // taxes: null,
        oldValues: {
          ...v.oldValues,
          ...oldValues,
        },
        trigger: 'preselectBonus',
      }));
      hideLoader();
      changePaymentProvider(pm[0]);
    } else {
      resetBonus();
      setState((v) => ({
        ...v,
        depositValue: defaultAmount,
        trigger: 'preselectBonus_else',
        // taxes: null,
      }));
      // hideLoader();
    }
  }, [state]);

  const createBonusCategories = React.useCallback(() => {
    if (!state.allBonuses) {
      return;
    }

    const bonusCategories: any = [];
    const ids: number[] = [];
    state.activePaymentProviders.map((provider) => {
      ids.push(provider.paymentProviderId);
      bonusCategories[provider.paymentProviderId] = [];
    });
    Object.keys(state.allBonuses).map((id: any) => {
      const bonus: Bonus = new Bonus(state.allBonuses[id]);
      if (bonus.getBonusId() !== 0) {
        let pm: number[] = bonus.getBonusPaymentMethodsRaw();
        if (!pm.length) {
          pm = ids;
        }
        pm.map((paymentMethod: any) => {
          // @ts-ignore
          if (bonusCategories[paymentMethod]) {
            const bonusInfo = getBonusInfo(bonus, false, paymentMethod);
            if (bonusInfo.id !== 0) {
              // @ts-ignore
              bonusCategories[paymentMethod].push(bonusInfo);
            }
          }
        });
      }
    });

    // check each bonus category if it has a bonus with -1 id and move it to the beginning
    Object.keys(bonusCategories).map((paymentMethod: any) => {
      const bonusCategory = bonusCategories[paymentMethod];
      const index = bonusCategory.findIndex((bonus: any) => bonus.id === -1);
      if (index !== -1) {
        const bonus = bonusCategory.splice(index, 1);
        bonusCategory.unshift(bonus[0]);
      }
    });

    if (state.bonusCategories !== bonusCategories) {
      setState((v) => ({
        ...v,
        bonusCategories: bonusCategories,
        bonuses: state.allBonuses,
        bonusLoading: false,
        trigger: 'createBonusCategories',
      }));
    }
  }, [state.allBonuses, state.activePaymentProviders]);

  React.useEffect(() => {
    if (deposit.bridger.cashierToken) {
      if (state.paymentProviderObject?.getType?.() === PaymentProvider.bridger) {
        // @ts-ignore
        state.paymentProviderObject?.setCashierToken?.(deposit.bridger.cashierToken);
      }
    }
  }, [deposit.bridger.cashierToken]);

  const selectCard = React.useCallback(
    (c: any) => {
      setState((v) => ({
        ...v,
        selectedCard: c,
        trigger: 'selectCard',
      }));
    },
    [state],
  );

  const changePaymentProvider = React.useCallback(
    (paymentProvider: number) => {
      const oldType = state.paymentProviderObject?.getType() ?? false;

      if (oldType == paymentProvider) {
        // setState((v) => ({
        //   ...v,
        //   isOpen: !v.isOpen,
        // }));
        return;
      }
      const paymentDetails = { ...state.paymentProviderObject?.getPaymentDetails() };
      if (!paymentDetails.amount) {
        paymentDetails.amount = state.depositValue;
      }
      state.paymentProviderObject?.destroy();
      // @ts-ignore
      const oldValues = state.oldValues?.[paymentProvider] ?? state.oldValues?.[oldType] ?? {};
      const selectedBonus = oldValues?.selectedBonus ?? oldValues?.selectedBonus ?? 0;

      const selectedBonusInfo = getBonusInfo(new Bonus(state.allBonuses?.[selectedBonus]), true, paymentProvider);

      const defaultState = {
        isOpen: true,
        tcAccepted: true,
        privacyAccepted: true,
        paymentProvider: paymentProvider,
        oldPaymentProvider: oldType,
        selectedBonus: selectedBonus,
        selectedBonusInfo: selectedBonusInfo,
        depositValue: oldValues?.amount ?? state.depositValue,
        currentStep: state.currentStep,
        oldValues: oldType
          ? {
              ...state.oldValues,
              [oldType]: {
                ...paymentDetails,
                selectedBonus: state.selectedBonus,
                selectedBonusInfo: selectedBonusInfo,
                depositValue: state.depositValue,
              },
            }
          : {},
      };
      // check if defaultState.depositValue is in the window.config.depositSettings[paymentProvider].defaultPresets;
      if (window.config?.depositSettings?.providers?.[paymentProvider]?.disabledInputAmount === '1') {
        // can't edit amount, so we need to use the default presets
        defaultState.depositValue =
          window.config?.depositSettings?.providers?.[paymentProvider]?.defaultAmount ??
          window.config?.depositSettings?.providers?.[paymentProvider]?.defaultPresets?.[0] ??
          defaultAmount;
        // TODO: select the closest amount rounded up from the default presets
      }
      switch (paymentProvider) {
        case PaymentProvider.bridger:
        case PaymentProvider.viva:
        case PaymentProvider.safecharge:
        case PaymentProvider.skrill:
        case PaymentProvider.betlion:
        case PaymentProvider.betlion_unitel:
        case PaymentProvider.betlion_telopay:
        case PaymentProvider.betlion_afrimoney:
        case PaymentProvider.betlion_ekwanza:
        case PaymentProvider.betlion_aki:
        case PaymentProvider.betlion_paypay:
        case PaymentProvider.betlion_pagaso:
        case PaymentProvider.betlion_multicaixa:
        case PaymentProvider.betlion_express:
        case PaymentProvider.betlion_paychangu:
          // if (DEPOSIT_PAYMENT_METHODS_FIRST === props.properties.depositType) {
          //   if (state.allSavedPaymentMethods) {
          //     defaultState.currentStep = initialStep[DEPOSIT_PAYMENT_PROVIDERS_FIRST];
          //   }
          // }
          // @ts-ignore
          setState((v) => ({
            ...v,
            ...defaultState,
            paymentProviderObject: getPaymentProviderByMethodType(paymentProvider, paymentDetails),
            trigger: 'changePaymentProvider_betlion',
          }));
          break;
        case PaymentProvider.okto:
          // @ts-ignore
          setState((v) => ({
            ...v,
            ...defaultState,
            tcAccepted: deposit?.okto?.customer?.okto_terms_accepted ?? false,
            privacyAccepted: deposit?.okto?.customer?.okto_terms_accepted ?? false,
            paymentProviderObject: getPaymentProviderByMethodType(paymentProvider, paymentDetails),
            trigger: 'changePaymentProvider_okto',
          }));
          break;
        case PaymentProvider.abon:
          // @ts-ignore
          setState((v) => ({
            ...v,
            ...defaultState,
            // taxes: null,
            paymentProviderObject: getPaymentProviderByMethodType(paymentProvider, paymentDetails),
            trigger: 'changePaymentProvider_abon',
          }));
          break;
        default:
          console.warn(`${paymentProvider} ${t('unknown payment provider')}`);
          break;
      }
    },
    [state],
  );

  const getPaymentProviderByMethodType = (paymentProvider: any, paymentDetails: any) => {
    let paymentProviderObject = null;
    const defaultData = {
      triggerSuccess: triggerSuccess,
      triggerPending: triggerPending,
      triggerError: triggerError,
      hideLoader: hideLoader,
      showLoader: showLoader,
      dispatch: dispatch,
      paymentDetails: paymentDetails,
      paymentProvider: paymentProvider,
    };

    switch (paymentProvider) {
      case PaymentProvider.bridger:
        paymentProviderObject = new Bridger({
          ...defaultData,
          hideInputsLoader: hideInputsLoader,
          showInputsLoader: showInputsLoader,
          selectCard: selectCard,
          settings: {
            blox: props.properties.bridgerBloxMainSettings,
            cards: props.properties.depositType === DEPOSIT_PAYMENT_METHODS_FIRST ? deposit.bridger.tokenList : false,
            validateCardInfo: validateCardInfo,
            unmaskedCVV: !(props?.properties?.maskedCVV?.toString() === 'true'),
          },
        });
        break;
      case PaymentProvider.viva:
        paymentProviderObject = new Viva({
          ...defaultData,
          selectCard: selectCard,
          settings: {
            cards: props.properties.depositType === DEPOSIT_PAYMENT_METHODS_FIRST ? deposit.viva.tokenList : false,
          },
        });
        break;
      case PaymentProvider.betlion:
      case PaymentProvider.betlion_unitel:
      case PaymentProvider.betlion_telopay:
      case PaymentProvider.betlion_afrimoney:
      case PaymentProvider.betlion_ekwanza:
      case PaymentProvider.betlion_aki:
      case PaymentProvider.betlion_paypay:
      case PaymentProvider.betlion_pagaso:
      case PaymentProvider.betlion_multicaixa:
      case PaymentProvider.betlion_express:
      case PaymentProvider.betlion_paychangu:
        paymentProviderObject = new Betlion({
          ...defaultData,
          settings: {
            channels: deposit.betLion.channels,
          },
        });
        break;
      case PaymentProvider.safecharge:
      case PaymentProvider.skrill:
        paymentProviderObject = new Safecharge({
          ...defaultData,
        });
        break;
      case PaymentProvider.okto:
        paymentProviderObject = new Okto({
          ...defaultData,
        });
        break;
      case PaymentProvider.abon:
        paymentProviderObject = new Abon({
          ...defaultData,
        });
        break;
      default:
        console.warn(`${paymentProvider} ${t('unknown payment provider')}`);
        break;
    }
    return paymentProviderObject;
  };

  React.useEffect(() => {
    if (state.paymentProviderObject?.getType() === PaymentProvider.betlion) {
      // @ts-ignore
      state.paymentProviderObject?.setChannels?.(deposit.betLion.channels);
    }
  }, [deposit.betLion.channels]);

  const initPaymentProvider = React.useCallback(() => {
    const initData = {
      currency: currentCurrency,
      paymentProvider: state.paymentProvider,
    };
    const initDataSavedPaymentMethods = {
      cardsFetched: props.properties.depositType === DEPOSIT_PAYMENT_METHODS_FIRST,
    };

    const bonus = new Bonus(state.selectedBonus ? state.allBonuses[state.selectedBonus] : false);

    const initApplePay = {
      amount: state.depositValue,
      currency: currentCurrency,
      bonusId: bonus.getBonusId(),
      rewardId: bonus.getRewardId(),
    };

    // reset bonus:
    switch (state.paymentProvider) {
      case PaymentProvider.bridger:
        state.paymentProviderObject?.init({
          ...initData,
          ...initDataSavedPaymentMethods,
          ...initApplePay,
          cashierToken: deposit.bridger.cashierToken,
          apCashierToken: deposit.bridgerApplePay.cashierToken,
          apCashierKey: deposit.bridgerApplePay.cashierKey,
        });
        if (!depositType) {
          // if depositType is set, it's a quick deposit!
          if (state.currentStep === 'amount') {
            if (props.properties.depositType === DEPOSIT_PAYMENT_METHODS_FIRST) {
              state.paymentProviderObject?.initInputsForNewCard();
            }
          } else {
            state.paymentProviderObject?.initInputsForSavedCards();
          }
        }
        break;
      case PaymentProvider.viva:
        state.paymentProviderObject?.init({
          ...initData,
          ...initDataSavedPaymentMethods,
          ...initApplePay,
        });
        break;
      case PaymentProvider.safecharge:
      case PaymentProvider.skrill:
      case PaymentProvider.abon:
      case PaymentProvider.betlion:
      case PaymentProvider.betlion_unitel:
      case PaymentProvider.betlion_telopay:
      case PaymentProvider.betlion_afrimoney:
      case PaymentProvider.betlion_ekwanza:
      case PaymentProvider.betlion_aki:
      case PaymentProvider.betlion_paypay:
      case PaymentProvider.betlion_pagaso:
      case PaymentProvider.betlion_multicaixa:
      case PaymentProvider.betlion_express:
      case PaymentProvider.betlion_paychangu:
        state.paymentProviderObject?.init({
          ...initData,
        });
        break;
      case PaymentProvider.okto:
        state.paymentProviderObject?.init({
          ...initData,
          getCustomer: deposit.okto.customer.okto_terms_accepted === null,
        });
        break;
      default:
        console.warn(`${state.paymentProvider} ${t('unknown payment provider')}`);
        break;
    }
  }, [state]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue: number = 0; /*e.target.value === '' ? 0 : (state.focusedInput ? e.target.value?.replace(/[^0-9.]/g,'') : extractNumberFromFormattedString(e.target.value)) || state.depositValue;*/
    if (e.target.value !== '') {
      if (state.focusedInput) {
        newValue = parseFloat(e.target.value?.replace(/[^0-9.]/g, ''));
      } else {
        newValue = extractNumberFromFormattedString(e.target.value);
      }
    }

    if (newValue === state.depositValue) {
      return;
    }
    // @ts-ignore
    setState((v) => ({
      ...v,
      depositValue: e.target.value === '' ? 0 : newValue || state.depositValue || 0,
      isUserDefinedAmount: true,
      taxes: null,
      trigger: 'onInputChange',
    }));
  };

  const onInputFocus = React.useCallback(() => {
    setState((v) => ({
      ...v,
      focusedInput: true,
      // depositValue: (state.depositValue),
      trigger: 'onInputFocus',
    }));
  }, [state]);

  const onInputBlur = React.useCallback(() => {
    // @ts-ignore
    setState((v) => ({
      ...v,
      focusedInput: false,
      // @ts-ignore
      depositValue: parseFloat(parseFloat(state.depositValue).toFixed(2)), // double parsing to ensure that we have maximum 2 decimal places
      trigger: 'onInputBlur',
    }));
  }, [state]);

  const onAlternativeInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // accept only phone numbers
    const regex = /^[0-9]*$/;
    if (!regex.test(e.target.value)) {
      return;
    }

    // @ts-ignore
    setState((v) => ({
      ...v,
      alternativeInfo: e.target.value,
      trigger: 'onAlternativeInfoChange',
    }));
  };

  const toggleHasAlternativeInfo = () => {
    setState((v) => ({
      ...v,
      hasAlternativeInfo: !v.hasAlternativeInfo,
      trigger: 'toggleHasAlternativeInfo',
    }));
  };

  const selectBonus = React.useCallback(
    (e: any, paymentProvider?: any) => {
      if (e === null) return;
      let value = 0;
      if (typeof e !== 'object') {
        value = e;
      } else {
        const el = e.currentTarget ?? (e.currentTarget as HTMLButtonElement);
        // get data-bonus-id from parent
        value = el?.parentElement?.getAttribute('data-bonus-id');
      }

      setState((v) => {
        let provider = paymentProvider;
        if (!provider) {
          provider = v.oldPaymentProvider;
          if (!provider) {
            provider = v.paymentProvider;
            if (!provider) {
              provider = state.defaultPaymentProvider;
            }
          }
        }
        const bonus = new Bonus(value ? v.bonuses?.[value] : false);
        const bonusInfo = getBonusInfo(bonus, true, provider, v);

        let amountToSelect = v.depositValue;
        if (v.selectedBonus !== value) {
          const minDeposit = v.selectedBonusInfo?.deposit?.min ?? defaultAmount;
          if ([0, minDeposit, defaultAmount].includes(v.depositValue)) {
            amountToSelect = Math.max(minDeposit, v.depositValue, defaultAmount);
          }
        }
        // calculate current step
        let currentStep = v.currentStep;
        // @ts-ignore
        if (value !== 0 && value !== false /*&& v.selectedBonus !== value*/ && currentStep === 'bonuses') {
          if (Object.values(initialStep).includes(v.lastSteps[v.lastSteps.length - 2])) {
            currentStep = v.lastSteps[v.lastSteps.length - 2];
          } else {
            currentStep = initialStep[props.properties.depositType];
          }
        }

        return {
          ...v,
          currentStep: currentStep,
          // paymentProvider: provider,
          selectedBonus: value,
          selectedBonusInfo: bonusInfo,
          depositValue: amountToSelect,
          oldValues: {
            ...v.oldValues,
            [provider]: {
              ...v.oldValues?.[provider],
              selectedBonus: value,
              selectedBonusInfo: bonusInfo,
              depositValue: v.depositValue,
            },
          },
          trigger: 'selectBonus',
        };
      });
    },
    [state],
  );

  const confirmDepositValue = React.useCallback(() => {
    if (state.depositValue > 0 && error == '') {
      const bonus = new Bonus(state.allBonuses[state.selectedBonus]);
      switch (state.paymentProvider) {
        case PaymentProvider.bridger:
          switch (state.currentStep) {
            case 'deposit-bridger':
              state.paymentProviderObject?.confirmPayment();
              return;
            case 'deposit-bridger-card':
              state.paymentProviderObject?.setPaymentDetails({
                ...state.paymentProviderObject?.getPaymentDetails(),
                card: false,
                cardInfo: {
                  holderName: state.cardInfo?.name ?? '',
                  cardNumber: state.cardInfo?.number ?? '',
                  expiryMonth: state.cardInfo?.expiry?.month,
                  expiryYear: state.cardInfo?.expiry?.year,
                  cvv: state.cardInfo?.cvv ?? '',
                },
              });
              state.paymentProviderObject?.confirmPayment();
              showLoader();
              return;
            case 'amount':
              if (props.properties.depositType === DEPOSIT_PAYMENT_METHODS_FIRST) {
                state.paymentProviderObject?.setPaymentDetails({
                  ...state.paymentProviderObject?.getPaymentDetails(),
                  card: false,
                  cardInfo: {
                    holderName: state.cardInfo?.name ?? '',
                    cardNumber: state.cardInfo?.number ?? '',
                    expiryMonth: state.cardInfo?.expiry?.month,
                    expiryYear: state.cardInfo?.expiry?.year,
                    cvv: state.cardInfo?.cvv ?? '',
                  },
                });
                state.paymentProviderObject?.confirmPayment();
                showLoader();
                return;
              } else {
                setState((v) => ({
                  ...v,
                  currentStep: 'deposit-bridger',
                  cardsLoading: true,
                  trigger: 'confirmDepositValue_amount!DEPOSIT_PAYMENT_METHODS_FIRST',
                }));
              }
              break;
            case initialStep[DEPOSIT_PAYMENT_METHODS_FIRST]:
              state.paymentProviderObject?.quickDeposit({
                card: state.selectedCard,
                amount: state.depositValue,
                bonusId: bonus.getBonusId(),
                rewardId: bonus.getRewardId(),
              });
              return;
            default:
              setState((v) => ({
                ...v,
                currentStep: 'deposit-bridger',
                cardsLoading: true,
                trigger: 'confirmDepositValue_default',
              }));
              break;
          }
          break;
        case PaymentProvider.safecharge:
        case PaymentProvider.skrill:
        case PaymentProvider.betlion:
        case PaymentProvider.betlion_unitel:
        case PaymentProvider.betlion_telopay:
        case PaymentProvider.betlion_afrimoney:
        case PaymentProvider.betlion_ekwanza:
        case PaymentProvider.betlion_aki:
        case PaymentProvider.betlion_paypay:
        case PaymentProvider.betlion_pagaso:
        case PaymentProvider.betlion_multicaixa:
        case PaymentProvider.betlion_paychangu:
          setState((v) => ({
            ...v,
            currentStep: 'deposit',
            trigger: 'confirmDepositValue_betlion',
          }));
          showLoader();
          break;
        case PaymentProvider.betlion_express:
          state.paymentProviderObject?.setPaymentDetails({
            ...state.paymentProviderObject?.getPaymentDetails(),
            alternatePhoneNumber: state.hasAlternativeInfo ? state.alternativeInfo : null,
          });
          setState((v) => ({
            ...v,
            currentStep: 'deposit-express',
            trigger: 'confirmDepositValue_betlion_express',
          }));
          showLoader();
          break;
        case PaymentProvider.viva:
          switch (state.currentStep) {
            case 'deposit-viva':
              state.paymentProviderObject?.confirmPayment();
              return;
            case 'deposit-viva-card':
              state.paymentProviderObject?.setPaymentDetails({
                ...state.paymentProviderObject?.getPaymentDetails(),
                card: true,
                cardInfo: {
                  holderName: state.cardInfo?.name ?? '',
                  cardNumber: state.cardInfo?.number ?? '',
                  expiryMonth: state.cardInfo?.expiry?.month,
                  expiryYear: state.cardInfo?.expiry?.year,
                  cvv: state.cardInfo?.cvv ?? '',
                },
              });
              state.paymentProviderObject?.confirmPayment();
              // showLoader();
              return;
            case initialStep[DEPOSIT_PAYMENT_METHODS_FIRST]:
              state.paymentProviderObject?.quickDeposit({
                card: state.selectedCard,
                amount: state.depositValue,
                bonusId: bonus.getBonusId(),
                rewardId: bonus.getRewardId(),
              });
              return;
            default:
              setState((v) => ({
                ...v,
                currentStep: 'deposit-viva',
                cardsLoading: true,
                trigger: 'confirmDepositValue_viva-default',
              }));
              break;
          }
          break;
        case PaymentProvider.okto:
          setState((v) => ({
            ...v,
            currentStep: 'deposit-okto',
            trigger: 'confirmDepositValue_okto',
          }));
          showLoader();
          break;
        case PaymentProvider.abon:
          setState((v) => ({
            ...v,
            currentStep: 'deposit-abon',
            trigger: 'confirmDepositValue_abon',
          }));
          showLoader();
          break;
        default:
          console.warn(`${state.paymentProvider} ${t('unknown payment provider')}`);
          break;
      }

      state.paymentProviderObject?.confirmDepositValue();
    }
  }, [state]);

  const onDepositButtonClick = React.useCallback(
    (e: any) => {
      const el = e.currentTarget ?? (e.currentTarget as HTMLButtonElement);
      // get data-value from button
      const value = el.getAttribute('data-value');

      if (value) {
        setState((v: any) => {
          let depositValue = Number(value);
          if (window.config?.depositSettings?.presetsType === 'incremental') {
            depositValue = Number(v.depositValue) + Number(value);
          }

          if (depositValue === state.depositValue) {
            return v;
          }

          return {
            ...v,
            depositValue: depositValue,
            isUserDefinedAmount: false,
            taxes: null,
            trigger: 'onDepositButtonClick',
          };
        });
      }
    },
    [state],
  );

  const validateCardInfo = React.useCallback(
    (field: string, data: any) => {
      window.parent.postMessage(
        {
          type: 'deposit-card-validation',
          field: field, // cvv, number, expiry
          data: data,
        },
        '*',
      );
      if (field === 'expiry') {
        // @ts-ignore
        setState((v) => ({
          ...v,
          cardInfo: {
            ...v.cardInfo,
            expiry: {
              ...v.cardInfo?.expiry,
              formatted: data.valid ? 'valid' : 'invalid',
            },
          },
          trigger: 'validateCardInfo_expiry',
        }));
      } else {
        // @ts-ignore
        setState((v) => ({
          ...v,
          cardInfo: {
            ...v.cardInfo,
            [field]: data.valid ? 'valid' : 'invalid',
          },
          trigger: 'validateCardInfo_!expiry',
        }));
      }
    },
    [state],
  );

  const getDepositButtons = () => {
    if (!state.paymentProviderObject) {
      changePaymentProvider(state.paymentProvider);
    }
    const buttons: AmountButtonInterface[] = state.paymentProviderObject?.getAmountPickerButtons(state, {
      currentCurrency: currentCurrency,
      setStateCallback: setState,
      onDepositButtonClick: onDepositButtonClick,
    });

    return buttons;
  };

  const onToggleLoading = () => {
    setState((v) => ({
      ...v,
      loading: !v.loading,
      trigger: 'onToggleLoading',
    }));
  };

  const onTCChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((v) => ({
        ...v,
        tcAccepted: event.target.checked,
        trigger: 'onTCChange',
      }));
    },
    [state],
  );

  const onPrivacyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState((v) => ({
      ...v,
      privacyAccepted: event.target.checked,
      trigger: 'onPrivacyChange',
    }));
  };

  const getDateOfBirth = (nin: string) => {
    if (!nin || nin.length < 13) {
      return '';
    }
    const year = nin.substr(1, 2);
    const month = nin.substr(3, 2);
    const day = nin.substr(5, 2);
    return `${day}.${month}.19${year}`;
  };

  const error: string = React.useMemo(() => {
    // @ts-ignore
    let error = deposit && deposit.error ? deposit.error : '';
    if (state.depositValue > 0) {
      const limits = state.paymentProviderObject?.getLimits() ?? { min: 0, max: 0 };
      // @ts-ignore
      if (state.maxLimit !== false && state.maxLimit < limits.max) {
        // @ts-ignore
        limits.max = state.maxLimit;
      }
      if (state.depositValue < limits.min) {
        // error = `${t('Minimum deposit amount is')} ${getFormattedCurrencyAmount({amount:limits.min, currency:currentCurrency, locale: '', digits: ''})}`;
        error = 'ERROR_MINIMUM_DEPOSIT_AMOUNT_{{MIN}}';
      } else if (state.depositValue > limits.max) {
        // error = `${t('Maximum deposit amount is')} ${getFormattedCurrencyAmount({amount:limits.max, currency:currentCurrency, locale: '', digits: ''})}`;
        error = 'ERROR_MAXIMUM_DEPOSIT_AMOUNT_{{MAX}}';
        // props.properties.text = `Maximum deposit amount is {{MAX}} {{CURRENCY}}`;
        // props.properties.placeholders = { MAX: limits.max, CURRENCY: formatCurrency(currentCurrency) };
      }
    }
    return error;
  }, [deposit, state.depositValue, state.maxLimit, state.paymentProviderObject]);

  let depositCode = '';
  let depositQRCode = '';
  let depositPaymentLink = '';
  if (state.paymentProviderObject?.getType() == PaymentProvider.okto) {
    depositCode = deposit.okto?.paymentCode;
  } else if (state.paymentProviderObject?.getType() == PaymentProvider.betlion_ekwanza) {
    depositCode = deposit.betLion.ekwanza?.paymentCode;
  } else if (state.paymentProviderObject?.getType() == PaymentProvider.betlion_paypay) {
    depositQRCode = deposit.betLion.paypay?.qrCode;
    depositPaymentLink = deposit.betLion.paypay?.url;
  }

  const skeletonCard = {
    token: '',
    type: 'skeleton',
    number: '',
    expiration_date: 0,
    verified: 0,
    selected: false,
    selectCard: () => null,
  };

  const isDepositDisabled = (paymentProviderId: any) => {
    if (state.focusedInput) {
      return true;
    }
    let depositDisabled = state.depositValue === 0 || error !== '';
    if (!state.privacyAccepted || !state.tcAccepted) {
      depositDisabled = true;
    }

    if (paymentProviderId == PaymentProvider.bridger) {
      if (state.currentStep === 'deposit-bridger-card') {
        depositDisabled =
          state.cardInfo?.cvv !== 'valid' ||
          state.cardInfo?.expiry?.formatted !== 'valid' ||
          state.cardInfo?.number !== 'valid' ||
          // @ts-ignore
          state.cardInfo?.name?.length < 3;
      } else if (state.currentStep === 'amount' && props.properties.depositType === DEPOSIT_PAYMENT_METHODS_FIRST) {
        depositDisabled =
          state.cardInfo?.cvv !== 'valid' ||
          state.cardInfo?.expiry?.formatted !== 'valid' ||
          state.cardInfo?.number !== 'valid';
      } else if (
        state.currentStep === 'deposit-bridger' ||
        state.currentStep === initialStep[DEPOSIT_PAYMENT_METHODS_FIRST]
      ) {
        depositDisabled = state.cardInfo?.cvv !== 'valid';
      }
    }
    return depositDisabled;
  };

  const tryAgain = React.useCallback(() => {
    dispatch(resetDeposit());

    dispatch(fetchPlayerPaymentMethods());
    if (state.paymentProviderObject?.getType() == PaymentProvider.okto) {
      dispatch(oktoGetCustomer());
    }
    setState((v) => ({
      ...initialState,
      activePaymentProviders: v.activePaymentProviders,
      paymentProvider: v.paymentProvider,
      depositValue: v.depositValue,
      depositValueWithoutTax: v.depositValueWithoutTax ?? v.depositValue,
      taxes: v.taxes ?? 0,
      selectedBonusInfo: v.selectedBonusInfo,
      selectedBonus: v.selectedBonus,
      bonusCategories: v.bonusCategories,
      bonuses: v.bonuses,
      allBonuses: v.allBonuses,
      bonusLoading: false,
      cardsLoading: false,
      allSavedPaymentMethods: v.allSavedPaymentMethods,
      lastSteps: [],
      initialized: false,
      trigger: 'tryAgain',
    }));
    hideLoader();
  }, [state]);

  let activeProviderOptions = {};
  const numberCurrency = getCurrencyNumberInfo({
    locale: '',
    amount: state.depositValue,
    currency: currentCurrency,
    digits: '',
  });
  const depositValue = state.focusedInput ? state.depositValue : numberCurrency.amount;

  const showBackButton = React.useCallback(() => {
    if (state.currentStep === initialStep[props.properties.depositType]) {
      return false;
    }
    if (
      state.currentStep === initialStep[DEPOSIT_PAYMENT_PROVIDERS_FIRST] &&
      props.properties.depositType === DEPOSIT_PAYMENT_METHODS_FIRST &&
      state.allSavedPaymentMethods.length === 0
    ) {
      return false;
    }

    return state.currentStep.indexOf('success') === -1;
  }, [initialStep, props.properties.depositType, state.allSavedPaymentMethods.length, state.currentStep]);

  const limits = state.paymentProviderObject?.getLimits() ?? { min: 0, max: 0 };
  // @ts-ignore
  if (state.maxLimit !== false && state.maxLimit < limits.max) {
    // @ts-ignore
    limits.max = state.maxLimit;
  }
  if (limits?.max < 0) {
    limits.max = 0;
  }

  const translatePlaceholders = {
    MAX: `${getFormattedCurrencyAmount({
      locale: '',
      amount: limits.max ?? 0,
      digits: '',
      currency: currentCurrency,
    })}`,
    MIN: `${getFormattedCurrencyAmount({
      locale: '',
      amount: limits.min ?? 0,
      digits: '',
      currency: currentCurrency,
    })}`,
  };

  const paychanguTimer = React.useMemo(
    () => ({
      start: deposit.betLion.paychangu ? new Date().getTime() / 1000 : 0,
      end: deposit.betLion.paychangu ? new Date().getTime() / 1000 + 3600 : 0,
    }),
    [deposit.betLion.paychangu],
  );

  // @ts-ignore
  const contextValue = {
    ...state,
    bonuses: state.bonusCategories?.[state.paymentProvider],
    depositButtons: getDepositButtons(),
    taxValue: state.taxes,
    selectBonus: selectBonus,
    confirmDepositValue: confirmDepositValue,
    currency: currentCurrency,
    depositValue: depositValue,
    onDepositValueChange: onInputChange,
    error: error,
    onToggleLoading: onToggleLoading,
    minimumDeposit: state.paymentProviderObject?.getLimits().min,
    amountButtonInfo:
      error == '' && state.depositValue > 0 && !isDepositDisabled(state.paymentProvider)
        ? {
            state: 'default',
            colorMapping: 'accent',
            buttonType: 'filled',
            size: 'large',
            style: 'text',
            shadow: 'depth_3',
            iconLeft: false,
            iconRight: false,
            ctaText: 'Deposit',
            icon: '',
          }
        : {
            state: 'disabled',
            colorMapping: 'inverted',
            buttonType: 'filled',
            size: 'large',
            style: 'text',
            shadow: 'none',
            iconLeft: false,
            iconRight: false,
            ctaText: 'Deposit',
            icon: '',
          },
    paymentProviderList: state.activePaymentProviders.map((provider: any) => {
      const depositDisabled = isDepositDisabled(provider.paymentProviderId);

      let hideTC = provider.paymentProviderId !== PaymentProvider.okto;
      if (provider.paymentProviderId == PaymentProvider.okto) {
        hideTC = !!deposit.okto?.customer?.okto_terms_accepted;
      }
      const applePayProvider = state?.paymentProviderObject?.getApplePay?.();
      let applePayButton = provider.applePayButton ?? { hide: true };
      if (applePayProvider) {
        // copy object to avoid errors;
        applePayButton = {
          ...applePayButton,
          onClick: applePayProvider.onApplePayButtonClicked,
        };
      }

      let options: any = {};
      switch (provider.paymentProviderId) {
        case PaymentProvider.betlion:
        case PaymentProvider.betlion_unitel:
        case PaymentProvider.betlion_telopay:
        case PaymentProvider.betlion_afrimoney:
        case PaymentProvider.betlion_ekwanza:
        case PaymentProvider.betlion_aki:
        case PaymentProvider.betlion_paypay:
        case PaymentProvider.betlion_pagaso:
        case PaymentProvider.betlion_multicaixa:
          options = {
            ...provider.channel,
            channelDetails: provider?.channel
              ? Object.entries(provider.channel)
                  .map((val) => {
                    if (Array.isArray(val) && val[0] && ALLOWED_CHANNEL_DETAILS_KEYS.includes(val[0])) {
                      return {
                        key: val[0].toLocaleUpperCase() + '_DEPOSIT_PROVIDER_CODE_KEYNAME',
                        value: val[1],
                      };
                    }
                    return null;
                  })
                  .filter((val) => val !== null)
              : [],
          };
          break;
        case PaymentProvider.betlion_paychangu:
          options = {
            ...provider.channel,
            channelDetails: provider?.channel
              ? Object.entries(provider.channel)
                  .map((val) => {
                    if (Array.isArray(val) && val[0] && ALLOWED_CHANNEL_DETAILS_KEYS.includes(val[0])) {
                      return {
                        key: val[0].toLocaleUpperCase() + '_DEPOSIT_PROVIDER_CODE_KEYNAME',
                        value: val[1],
                      };
                    }
                    return null;
                  })
                  .filter((val) => val !== null)
              : [],
            accountInfo: deposit.betLion.paychangu,
            timer: paychanguTimer,
          };
          break;
        case PaymentProvider.betlion_express:
          options = {
            ...provider.channel,
            channelDetails: provider?.channel
              ? Object.entries(provider.channel)
                  .map((val) => {
                    if (Array.isArray(val) && val[0] && ALLOWED_CHANNEL_DETAILS_KEYS.includes(val[0])) {
                      return {
                        key: val[0].toLocaleUpperCase() + '_DEPOSIT_PROVIDER_CODE_KEYNAME',
                        value: val[1],
                      };
                    }
                    return null;
                  })
                  .filter((val) => val !== null)
              : [],
            alternativeInfo: state.alternativeInfo,
            changeAlternativeInfo: onAlternativeInfoChange,
            hasAlternativeInfo: state.hasAlternativeInfo,
            toggleHasAlternativeInfo: toggleHasAlternativeInfo,
            phoneNumber: profile?.phone ?? '',
          };
          break;
        case PaymentProvider.okto:
          options = {
            tcAccepted: state.tcAccepted,
            privacyAccepted: state.privacyAccepted,
          };
          break;
        case PaymentProvider.bridger:
          options = {
            depositType: props.properties.depositType,
          };
          break;
        default:
          break;
      }

      options.provider_status = provider.status;

      if (provider.paymentProviderId === state.paymentProvider) {
        activeProviderOptions = options;
      }

      return {
        id: provider.paymentProviderId,
        currentStep: `amount-${paymentProviderNameById[provider.paymentProviderId]?.toLowerCase()}`,
        buttons: getDepositButtons(),
        selectedBonusInfo: state.selectedBonusInfo ?? getBonusInfo(new Bonus(false), false, provider.paymentProviderId),
        bonusCounter: state.bonusCategories?.[provider.paymentProviderId]?.length ?? 0,
        taxValue: state.taxes,
        currency: currentCurrency,
        value: depositValue,
        error: error,
        onValueChange: onInputChange,
        onAmountInputFocus: onInputFocus,
        onAmountInputBlur: onInputBlur,
        // currencyPosition: state.currencyPosition,
        changePaymentProvider: () => {
          // asd needs to be fixed?
          changePaymentProvider(provider.paymentProviderId);
        },
        isOpen: state.paymentProvider == provider.paymentProviderId && state.isOpen,
        inputAmountDisabled:
          window.config?.depositSettings?.providers?.[provider.paymentProviderId]?.disabledInputAmount === '1',
        onTCChange: onTCChange,
        onPrivacyChange: onPrivacyChange,
        hideTC: hideTC,
        confirmButton: {
          state: depositDisabled ? 'disabled' : 'default',
          colorMapping: depositDisabled ? 'inverted' : 'primary',
          buttonType: 'filled',
          size: 'small',
          style: 'text',
          shadow: depositDisabled ? 'none' : 'depth_2',
          loading: state.loading,
          iconLeft: false,
          iconRight: false,
          ctaText: 'Deposit',
          icon: '',
          onClick: depositDisabled ? () => 0 : confirmDepositValue,
          hide: provider.mainButton?.hide ?? false,
        },
        applePayButton: applePayButton ? applePayButton : { hide: true },
        hideProvider: provider.mainButton?.hide && applePayButton?.hide,
        options: options,
        translatePlaceholders: translatePlaceholders,

        selectCard: () => {
          // asd needs to be fixed?
          changePaymentProvider(provider.paymentProviderId);
        },
        selected: state.paymentProvider == provider.paymentProviderId && state.isOpen,
        type: paymentProviderNameById[provider.paymentProviderId]?.toLowerCase(),
        label: `DEPOSIT.PAYMENT-PROVIDER.${paymentProviderNameById[provider.paymentProviderId]?.toUpperCase()}`,
      };
    }),
    providerWithCard: {
      cards: state.cardsLoading
        ? [
            skeletonCard,
            skeletonCard,
            skeletonCard,
            skeletonCard,
            skeletonCard,
            skeletonCard,
            skeletonCard,
            skeletonCard,
          ]
        : (state.paymentProviderObject?.getCards() ?? []),
      isSelectedCard: state.paymentProviderObject?.getPaymentDetails()?.card ?? false,
      emptyFn: () => 0,
      useNewCard: () => {
        // asd needs to be fixed?
        state.paymentProviderObject?.setPaymentDetails({
          ...state.paymentProviderObject?.getPaymentDetails(),
          card: false,
        });
        setState((v) => ({
          ...v,
          currentStep: state.paymentProvider == PaymentProvider.bridger ? 'deposit-bridger-card' : 'deposit-viva-card',
          trigger: 'useNewCard',
        }));
      },
      onCardNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        // asd needs to be fixed?
        const oldValue = state.cardInfo?.number ?? '';
        const value = e.target.value;
        let newValue = oldValue;
        if (value.match(/^[0-9\s]*$/) && value.length <= 19) {
          newValue = value
            .replace(/\D/g, '')
            .replace(/(\d{4})(\d)/, '$1 $2')
            .replace(/(\d{4})(\d)/, '$1 $2')
            .replace(/(\d{4})(\d)/, '$1 $2');
        }
        if (newValue !== oldValue) {
          // @ts-ignore
          setState((v) => ({
            ...v,
            cardInfo: {
              ...v.cardInfo,
              number: newValue,
            },
            trigger: 'onCardNumberChange',
          }));
        }
      },
      onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        // asd needs to be fixed?
        const oldValue = state.cardInfo?.name ?? '';
        const value = e.target.value;
        let newValue = oldValue;
        if (value.length <= 50) {
          // check if value is alphanumeric
          if (value.match(/^[0-9a-zA-Z-\s'.]*$/)) {
            newValue = value;
          } else {
            if (value.length === 1) {
              newValue = '';
            }
          }
        }
        // @ts-ignore
        if (newValue !== oldValue) {
          // @ts-ignore
          setState((v) => ({
            ...v,
            cardInfo: {
              ...v.cardInfo,
              name: newValue,
            },
            trigger: 'onNameChange',
          }));
        }
      },
      onExpiryDateChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        // asd needs to be fixed?
        let value = e.target.value?.replace(/\D/g, '');
        let month: string;
        let year: string;
        switch (value.length) {
          case 1:
            if (value.length === 1 && parseInt(value) > 1) {
              value = '0' + value;
            }
            month = value;
            break;
          case 2:
            if (parseInt(value[0]) === 1 && parseInt(value[1]) > 2) {
              month = '0' + value[0];
              year = value[1];
              value = '0' + value[0] + '/' + value[1];
            } else {
              month = value;
            }
            break;
          case 3:
            month = value[0] + value[1];
            year = value[2];
            value = value[0] + value[1] + '/' + value[2];
            break;
          case 4:
            month = value[0] + value[1];
            year = value[2] + value[3];
            value = value[0] + value[1] + '/' + value[2] + value[3];
            break;
          default:
            month = state.cardInfo?.expiry?.month ?? '';
            value = state.cardInfo?.expiry?.formatted ?? '';
            year = state.cardInfo?.expiry?.year ?? '';
            break;
        }
        // @ts-ignore
        setState((v) => ({
          ...v,
          cardInfo: {
            ...v.cardInfo,
            expiry: {
              month: e.target.value.split('/')[0],
              year: e.target.value.split('/')[1],
              formatted: value,
            },
          },
          trigger: 'onExpiryDateChange',
        }));
      },
      onSecurityCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        // asd needs to be fixed?
        let value = state.cardInfo?.cvv ?? '';
        if (e.target?.value.match(/^[0-9]*$/) && e.target?.value?.length <= 4) {
          value = e.target.value;
        } else {
          value = state.cardInfo?.cvv ?? '';
        }

        // @ts-ignore
        setState((v) => ({
          ...v,
          cardInfo: {
            ...v.cardInfo,
            cvv: value,
          },
          trigger: 'onSecurityCodeChange',
        }));
      },
      cardNumber: state.cardInfo?.number ?? '',
      name: state.cardInfo?.name ?? '',
      expiryDate: state.cardInfo?.expiry?.formatted ?? '',
      securityCode: state.cardInfo?.cvv ?? '',
      year: state.cardInfo?.expiry?.year ?? '',
      month: state.cardInfo?.expiry?.month ?? '',
    },
    toAmount: toAmount,
    toBonuses: toBonuses,
    depositCode: depositCode,
    depositQRCode: depositQRCode,
    depositPaymentLink: depositPaymentLink,
    playerName: profile?.first_name + ' ' + profile?.last_name,
    playerNin: profile?.nin,
    playerDateOfBirth: getDateOfBirth(profile?.nin),
    allSavedPaymentMethods: state.allSavedPaymentMethods.map((method: Card) => {
      return {
        ...method,
        selected: method.token === state.selectedCard?.token,
      };
    }),
    onBack: goBack,
    onClose: () => {
      const isFgm = ExpiringLocalStorage.get('fgm');
      if (isFgm) {
        ExpiringLocalStorage.remove('fgm');

        navigate(isFgm);
      } else {
        navigate('/');
      }
    },
    goToAllSavedMethods: goToAllSavedMethods,
    tryAgain: tryAgain,
    translatePlaceholders: translatePlaceholders,
    activeProviderOptions: activeProviderOptions,
    depositType: props.properties.depositType,
    showWalletBalance: props?.properties?.showWalletBalance?.toString() === 'true',
    walletBalance: total,
    showBackButton: showBackButton(),
  };
  React.useEffect(() => {
    /**
     * idei:
     * - nu se schimba providerul de plata cand se apeleaza select card;
     * - cand se confirma, in functie de ce payment provider are metoda de plata asignata sa se instantieze noul obiect
     *    - 1. init()
     *    - 2. confirmDepositValue() -> cheama auth in interior
     *    - probabil setPaymentDetails, desi e posibil sa se transfere informatiile la changePaymentProvider;
     *    - probabil confirmPayment();
     */
    // log stack trace
    // console.trace();
    console.log('trigger', state.trigger, state.currentStep, state);
  }, [state.trigger]);

  console.log('[Deposit] contextValue ', contextValue.activeProviderOptions, deposit.betLion.paychangu);
  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Deposit;
